import React from 'react';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import analyticsFirstReplyTimeTooltipImage from '../../assets/images/analyticsFirstReplyTimeTooltipIllustration.png';
import analyticsHandleTimeTooltipImage from '../../assets/images/analyticsHandleTimeTooltipIllustration.png';
import analyticsReplyTimeTooltipImage from '../../assets/images/analyticsReplyTimeTooltipIllustration.png';
import analyticsResolutionTimeTooltipImage from '../../assets/images/analyticsResolutionTimeTooltipIllustration.png';
import {helpCenterUrl} from '../../store/stateless/navigationActions';
import {AnalyticsTooltipCalculationContent} from '../components/reportElements/tooltips/analyticsTooltipCalculationContent';
import {
  conversationStagesHelpCenterUrl,
  conversationStagesMetricsGlossaryHelpCenterUrl,
  segmentsHelperCenterUrl,
  ticketStatusHelpCenterUrl,
} from './reportHelpers/analyticsReportHelpers';

/**
 * Shared tooltips for analytics metrics.
 * A tooltip can be reused for multiple metric variations (scalar, graph,...)
 * TODO finish migrating all shared tooltips here?
 */

export const newConversationsTooltip = {
  title: <Trans id="analytics-metric-tooltip-new-conversations-title">New conversations</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-new-conversations">
        Track the volume of new conversations to be worked on.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-new-conversations-calculation">
            Total number of new{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segments
            </a>{' '}
            created from a new inbound or outbound message during the time period.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/m1hlqjy/new-conversations`,
};

export const messagesReceivedTooltip = {
  title: <Trans id="analytics-metric-tooltip-messages-received-title">Messages received</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-messages-received">Track the volume of inbound messages.</Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-messages-received-calculation">
            Total number of inbound messages received during the time period.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv/messages-received`,
};

export const messagesSentTooltip = {
  title: <Trans id="analytics-metric-tooltip-messages-sent-title">Messages sent</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-messages-sent">Track the volume of outbound messages.</Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-messages-sent-calculation">
            Total number of outbound messages sent during the time period.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/60hlqgg/messages-sent`,
};

export const stageResolvedTooltip = {
  title: <Trans id="analytics-metric-tooltip-resolved-title">Resolved conversations</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-resolved">
        Track conversations for which the work is considered done.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-stage-resolved-calculation">
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              Conversation segments
            </a>{' '}
            that were archived for the last time during the selected time period, and remained archived since.
            Additional conditions might apply depending on your{' '}
            <a href={conversationStagesHelpCenterUrl} target="_blank" rel="noreferrer">
              conversation stages
            </a>{' '}
            rule.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${conversationStagesMetricsGlossaryHelpCenterUrl}#resolved_conversations`,
};

export const ticketResolvedTooltip = {
  title: <Trans id="analytics-metric-tooltip-resolved-title">Resolved conversations</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-resolved">
        Track conversations for which the work is considered done.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-resolved-calculation">
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              Conversation segments
            </a>{' '}
            that were resolved for the last time during the selected time period, and remained resolved since.
            If a resolved conversation is moved out of an inbox with{' '}
            <a href={ticketStatusHelpCenterUrl} target="_blank" rel="noreferrer">
              ticketing
            </a>
            , it stays resolved.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `https://help.front.com/en/articles/1405056`,
};

export const stageUnresolvedTooltip = {
  title: <Trans id="analytics-metric-tooltip-unresolved-title">Unresolved conversations</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-unresolved">Track conversations that are being worked on.</Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-unresolved-stage-calculation">
            New and reopened{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segments
            </a>{' '}
            during the time period.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${conversationStagesMetricsGlossaryHelpCenterUrl}#unresolved_conversations`,
};

export const ticketUnresolvedTooltip = {
  title: <Trans id="analytics-metric-tooltip-unresolved-title">Unresolved conversations</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-unresolved">Track conversations that are being worked on.</Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-unresolved-calculation">
            <a href={ticketStatusHelpCenterUrl} target="_blank" rel="noreferrer">
              Conversation segments
            </a>{' '}
            that were active during the selected time period, and are not considered resolved.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `https://help.front.com/en/articles/1405120`,
};

export const ignoredTooltip = {
  title: <Trans id="analytics-metric-tooltip-ignored-title">No action needed</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-ignored">
        Track conversations that don&apos;t require any work (ex. newsletters).
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-ignored-calculation">
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              Conversation segments
            </a>{' '}
            that were archived with no outbound reply during the selected time period, and that remained
            archived since. Additional conditions might apply depending on your{' '}
            <a href={conversationStagesHelpCenterUrl} target="_blank" rel="noreferrer">
              conversation stages
            </a>{' '}
            rule.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${conversationStagesMetricsGlossaryHelpCenterUrl}#no_action_needed`,
};

export const stageResolutionTimeTooltip = {
  title: <Trans id="analytics-metric-tooltip-resolution-time-title">Resolution time</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-resolution-time">
        Track the time it takes to resolve conversations.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-resolution-time-stage-calculation">
            Time from the start of a{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segment
            </a>{' '}
            to its last resolution. The segment must have been resolved for the last time during the selected
            time period, and remained resolved since. Takes business hours and shifts into consideration.
          </Trans>
        }
        img={analyticsResolutionTimeTooltipImage}
      />
    </>
  ),
  learnMoreUrl: `${conversationStagesMetricsGlossaryHelpCenterUrl}#resolution_time_avg`,
};

export const ticketResolutionTimeTooltip = {
  title: <Trans id="analytics-metric-tooltip-resolution-time-title">Resolution time</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-resolution-time">
        Track the time it takes to resolve conversations.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-resolution-time-calculation">
            Time from the start of a resolved{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segment
            </a>{' '}
            to its last resolution. Takes business hours and shifts into consideration.
          </Trans>
        }
        img={analyticsResolutionTimeTooltipImage}
      />
    </>
  ),
  learnMoreUrl: `https://help.front.com/en/articles/1405184`,
};

export const stageRepliesToResolutionTooltip = {
  title: <Trans id="analytics-metric-tooltip-replies-to-resolution-time-title">Replies to resolution</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-replies-to-resolution-time">
        Track the number of replies it takes to resolve conversations.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-replies-to-resolution-time-calculation">
            Number of outbound replies in a resolved{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segment
            </a>
            .
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${conversationStagesMetricsGlossaryHelpCenterUrl}#replies_to_resolution_avg`,
};

export const ticketRepliesToResolutionTooltip = {
  title: <Trans id="analytics-metric-tooltip-replies-to-resolution-time-title">Replies to resolution</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-replies-to-resolution-time">
        Track the number of replies it takes to resolve conversations.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-replies-to-resolution-time-calculation">
            Number of outbound replies in a resolved{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segment
            </a>
            .
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `https://help.front.com/en/articles/1405248`,
};

export const stageResolvedFirstReplyTooltip = {
  title: <Trans id="analytics-metric-tooltip-resolved-first-reply-title">Resolved on first reply</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-resolved-first-reply">
        Track conversations resolved in only one reply.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-resolved-first-reply-calculation">
            Resolved{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segments
            </a>{' '}
            with only one outbound reply.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${conversationStagesMetricsGlossaryHelpCenterUrl}#resolved_on_first_reply`,
};

export const ticketResolvedFirstReplyTooltip = {
  title: <Trans id="analytics-metric-tooltip-resolved-first-reply-title">Resolved on first reply</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-resolved-first-reply">
        Track the percentage of conversations resolved in only one reply.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-resolved-first-reply-calculation">
            Resolved{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segments
            </a>{' '}
            with only one outbound reply.
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `https://help.front.com/en/articles/1405312`,
};

export const replyTimeTooltip = {
  title: <Trans id="analytics-metric-tooltip-reply-time-title">Reply time</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-reply-time">
        Track the time a customer has to wait before receiving a reply.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-reply-time-calculation">
            Time between receiving an inbound message and sending an outbound reply in a given{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segment
            </a>
            . Takes business hours and shifts into consideration.
          </Trans>
        }
        img={analyticsReplyTimeTooltipImage}
      />
    </>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/q6hlqr6/reply-time-avg`,
};

export const firstReplyTimeTooltip = {
  title: <Trans id="analytics-metric-tooltip-first-reply-time-title">First reply time</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-first-reply-time">
        Track the time a customer has to wait before receiving the first reply.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-first-reply-time-calculation">
            Time between receiving the first inbound message and sending the first outbound reply in a given{' '}
            <a href={segmentsHelperCenterUrl} target="_blank" rel="noreferrer">
              conversation segment
            </a>
            . Takes business hours and shifts into consideration.
          </Trans>
        }
        img={analyticsFirstReplyTimeTooltipImage}
      />
    </>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/x2hlq1a/first-reply-time-avg`,
};

export const handleTimeTooltip = {
  title: <Trans id="analytics-metric-tooltip-handle-time-title">Handle time</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-handle-time">
        Track the time teammates spend crafting a message before it gets sent.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-handle-time-calculation">
            Time between starting an outbound reply draft and sending it. Doesn&apos;t take business hours or
            shifts into consideration. Some exceptions apply.
          </Trans>
        }
        img={analyticsHandleTimeTooltipImage}
      />
    </>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/35hlqpm/handle-time-avg`,
};

export const activeTeammatesTooltip = {
  title: <Trans id="analytics-metric-tooltip-active-teammates-title">Active teammates</Trans>,
  tooltipContent: (
    <>
      <Trans id="analytics-metric-tooltip-active-teammates">
        Track how many teammates are active on Front.
      </Trans>
      <AnalyticsTooltipCalculationContent
        content={
          <Trans id="analytics-metric-tooltip-active-teammates-calculation">
            Number of teammates that took at least one action during the time period of the report (ex. reply,
            archive, comment, tag, etc.).
          </Trans>
        }
      />
    </>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/q6h1ms6`,
};
